// src/features/datev/components/DatevEau.tsx
//
// UI für hr:eau (elektronische Arbeitsunfähigkeitsbescheinigung).
// Vier Bereiche:
//   1. Berechtigungsprüfung (MUST: Erfolg + Fehlerfall)
//   2. Berechtigungstest (für Audit, einklappbar hinter Info-Symbol)
//   3. Neue eAU-Anfragen (Mitarbeiterauswahl aus dem Spiegel, Sammelanlage,
//      Compliance-Bestätigung §5 EntgFG, Ansprechpartner für Kassenrückfragen)
//   4. Anfrage-Historie mit Feedback-Abruf und Stornierung

import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertCircle, CheckCircle, Search, RefreshCw, XCircle,
  Plus, FileText, Trash2, Activity, AlertTriangle, Info, X, Download
} from 'lucide-react';
import { supabase } from '../../../shared/services/supabase';
import { netlifyFetch } from '../../../shared/services/netlifyFetch';
import {
  datevEauService,
  DatevEauSource,
  DatevEauFlag,
  DatevEauFeedback,
  DatevEauContactPerson,
  DATEV_EAU_FLAG_LABELS,
  DATEV_EAU_FLAG_COLORS,
  XTAX_EAU_TELEFON,
  XTAX_EAU_KONTAKT_EMAIL,
  XTAX_EAU_BENACHRICHTIGUNG_EMAIL,
  sanitizeEauTelefon,
  sanitizeEauText,
  splitStrasseUndHausnummer
} from '../../../services/datev/DatevEauService';
import { eauNachweisService } from '../../../services/datev/EauNachweisService';
import { EauUebersicht } from './EauUebersicht';

interface DatevEauProps {
  customerId: string;
  beraternummer: string;
  mandantennummer: string;
}

interface EauHistoryRow {
  id: string;
  client_id: string;
  personnel_number: string;
  eau_uuid: string;
  source: string;
  start_work_incapacity: string;
  notification_email: string | null;
  follow_up_certification: boolean;
  state: string;
  last_feedback: any;
  last_feedback_fetched_at: string | null;
  feedback_fetch_count: number;
  compliance_confirmed: boolean;
  created_at: string;
  cancelled_at: string | null;
  abgelehnt_am: string | null;
  ablehnung_code: string | null;
  ablehnung_text: string | null;
}

/** Mitarbeiter aus dem DATEV-Spiegel, reduziert auf die für die eAU nötigen Felder. */
interface SpiegelMitarbeiter {
  personalnummer: string;
  vorname: string | null;
  nachname: string | null;
  betrieblicheNummer: string | null;
  eintritt: string | null;
  austritt: string | null;
  kvBeitragsgruppe: number | null;
}

/** Eine Zeile im Anlage-Korb. */
interface AnlageZeile {
  /** Eindeutig innerhalb des Korbs, damit Zeilen ohne Spiegel-Treffer auch gehen. */
  key: string;
  personalnummer: string;
  name: string;
  betrieblicheNummer: string | null;
  eintritt: string | null;
  austritt: string | null;
  kvBeitragsgruppe: number | null;
  /** Aus dem Spiegel übernommen (false) oder manuell erfasst (true). */
  manuell: boolean;
  auBeginn: string;
  /**
   * Gewünschtes Ende des Krankheitszeitraums. Geht NICHT an DATEV, der
   * Anforderungsdatensatz kennt kein Enddatum. Wird bei uns als Befund
   * hinterlegt, damit die Rückmeldung der Kasse dagegen abgeglichen werden
   * kann. Ohne diese Angabe gilt ein Vorgang als erledigt, sobald die Kasse
   * irgendetwas bestätigt, auch wenn das nur ein Teilzeitraum ist.
   */
  auEnde: string;
  folgebescheinigung: boolean;
  ergebnis: 'offen' | 'erfolg' | 'fehler';
  ergebnisText: string | null;
  eauUuid: string | null;
}

type ViewMode = 'overview' | 'create';

export const DatevEau: React.FC<DatevEauProps> = ({
  customerId,
  beraternummer,
  mandantennummer
}) => {
  const clientId = `${beraternummer}-${mandantennummer}`;

  // ============================================
  // STATE: Berechtigungsprüfung
  // ============================================
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [availableClients, setAvailableClients] = useState<{ consultant: string; ranges: string[]; count: number }[] | null>(null);

  // Audit-Test (ungültige Berater-/Mandantennr.)
  const [testPanelOffen, setTestPanelOffen] = useState(false);
  const [testBerater, setTestBerater] = useState('');
  const [testMandant, setTestMandant] = useState('');
  const [testResult, setTestResult] = useState<'success' | 'failure' | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<number | null>(null);
  const [testRunning, setTestRunning] = useState(false);

  // ============================================
  // STATE: Mitarbeiterspiegel und Anlage
  // ============================================
  const [view, setView] = useState<ViewMode>('overview');
  const [spiegel, setSpiegel] = useState<SpiegelMitarbeiter[]>([]);
  const [spiegelLoading, setSpiegelLoading] = useState(false);
  const [spiegelFehler, setSpiegelFehler] = useState<string | null>(null);
  const [suchbegriff, setSuchbegriff] = useState('');
  const [manuellePnr, setManuellePnr] = useState('');

  const [korb, setKorb] = useState<AnlageZeile[]>([]);
  const [newSource, setNewSource] = useState<DatevEauSource>('LUG');
  const [newEmail, setNewEmail] = useState(XTAX_EAU_BENACHRICHTIGUNG_EMAIL);
  const [sammelDatum, setSammelDatum] = useState('');
  const [complianceChecked, setComplianceChecked] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Ansprechpartner für Rückfragen der Krankenkasse
  const [kontaktperson, setKontaktperson] = useState<DatevEauContactPerson | null>(null);
  const [kontaktFehler, setKontaktFehler] = useState<string | null>(null);
  const [angemeldeteEmail, setAngemeldeteEmail] = useState<string | null>(null);

  // ============================================
  // STATE: Historie + Aktionen
  // ============================================
  const [history, setHistory] = useState<EauHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [actionUuid, setActionUuid] = useState<string | null>(null);
  const [actionType, setActionType] = useState<'feedback' | 'cancel' | null>(null);
  const [detailRowId, setDetailRowId] = useState<string | null>(null);
  // Zeile, bei der die Originalmeldung von DATEV eingeblendet ist.
  const [technikRowId, setTechnikRowId] = useState<string | null>(null);
  const [nachweisLaeuft, setNachweisLaeuft] = useState(false);

  const ladeNachweis = async () => {
    setNachweisLaeuft(true);
    try {
      const ergebnis = await eauNachweisService.generateNachweis(customerId, clientId);
      if (!ergebnis.success) {
        alert(`Übersicht konnte nicht erstellt werden: ${ergebnis.error}`);
      }
    } finally {
      setNachweisLaeuft(false);
    }
  };
  // Zeitpunkt des letzten Neuladens der Historie. Dient nur der sichtbaren
  // Rückmeldung am "Liste aktualisieren"-Button, damit der Klick nicht
  // wirkungslos erscheint.
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  // ============================================
  // BERECHTIGUNGSPRÜFUNG
  // ============================================

  const checkAccess = useCallback(async () => {
    setAccessChecked(false);
    setAccessError(null);
    try {
      await datevEauService.checkAccess({ customerId, clientId });
      setHasAccess(true);
      setAccessChecked(true);
    } catch (err: any) {
      setHasAccess(false);
      setAccessChecked(true);
      if (err.requires_reconnect) {
        setAccessError('Keine DATEV-Verbindung. Bitte zunächst im Tab "Verbindung" mit DATEV verbinden.');
      } else if (err.error) {
        setAccessError(err.error);
      } else {
        setAccessError('Berechtigung konnte nicht geprüft werden.');
      }

      // Audit-Auflage 2: Bei Berechtigungsfehler freigegebene Mandanten anzeigen
      try {
        const res = await netlifyFetch(
          `https://intranet.xtax.de/.netlify/functions/datev-eau/clients?customerId=${customerId}`
        );
        if (res.ok) {
          const data = await res.json();
          if (data.groups && data.groups.length > 0) {
            setAvailableClients(data.groups);
          }
        }
      } catch (e) {
        // Mandanten-Liste optional
      }
    }
  }, [customerId, clientId]);

  useEffect(() => { checkAccess(); }, [checkAccess]);

  // ============================================
  // AUDIT-TEST (ungültige Kombination)
  // ============================================

  const runAccessTest = async () => {
    if (!testBerater || !testMandant) return;
    setTestRunning(true);
    setTestResult(null);
    setTestError(null);
    setTestStatus(null);

    try {
      await datevEauService.checkAccessForClient({ customerId, clientId }, `${testBerater}-${testMandant}`);
      setTestResult('success');
      setTestStatus(200);
    } catch (err: any) {
      setTestResult('failure');
      setTestError(err.error || 'Unbekannter Fehler');
      // HTTP-Status aus datev_error ableiten wenn verfügbar
      if (err.datev_error?.includes('401')) setTestStatus(401);
      else if (err.datev_error?.includes('403')) setTestStatus(403);
      else if (err.datev_error?.includes('404')) setTestStatus(404);
      else setTestStatus(400);
    } finally {
      setTestRunning(false);
    }
  };

  // ============================================
  // ANSPRECHPARTNER LADEN
  // ============================================
  //
  // Die Krankenkasse wendet sich bei Rückfragen an diese Angaben. Firmenname und
  // Anschrift kommen aus invoice_settings, der Name aus xtax_staff anhand der
  // angemeldeten E-Mail. Telefon und E-Mail sind im Service festgelegt.

  const loadKontaktperson = useCallback(async () => {
    setKontaktFehler(null);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email || null;
      setAngemeldeteEmail(email);

      let anzeigeName = email || '';
      if (email) {
        const { data: staff } = await supabase
          .from('xtax_staff')
          .select('full_name')
          .eq('email', email)
          .maybeSingle();
        if (staff?.full_name) anzeigeName = staff.full_name;
      }

      const { data: firma, error: firmaError } = await supabase
        .from('invoice_settings')
        .select('firma_name, firma_strasse, firma_plz, firma_ort')
        .limit(1)
        .maybeSingle();

      if (firmaError || !firma) {
        setKontaktperson(null);
        setKontaktFehler('Firmenstammdaten konnten nicht geladen werden. Die Anfrage geht ohne Ansprechpartner an DATEV.');
        return;
      }

      const anschrift = splitStrasseUndHausnummer(firma.firma_strasse || '');

      // DATEV lässt in diesen Feldern nur Buchstaben, Ziffern, Leerzeichen sowie
      // Bindestrich, Apostroph, Klammern und Punkt zu. Ein nicht bereinigter Wert
      // führt zu EAU V-RE-CONT002 und damit zur Ablehnung der gesamten Anfrage.
      const kontakt: DatevEauContactPerson = {
        name: sanitizeEauText(anzeigeName),
        telephone: sanitizeEauTelefon(XTAX_EAU_TELEFON),
        company_name: sanitizeEauText(firma.firma_name || ''),
        postal_code: sanitizeEauText(firma.firma_plz || ''),
        city: sanitizeEauText(firma.firma_ort || ''),
        street: sanitizeEauText(anschrift.street),
        house_number: anschrift.house_number ? sanitizeEauText(anschrift.house_number) : undefined,
        email: XTAX_EAU_KONTAKT_EMAIL
      };

      if (!kontakt.name || !kontakt.company_name || !kontakt.postal_code || !kontakt.city || !kontakt.street || !kontakt.telephone) {
        setKontaktperson(null);
        setKontaktFehler('Ansprechpartner unvollständig (Name, Firma, Strasse, PLZ, Ort und Telefon sind Pflicht). Die Anfrage geht ohne Ansprechpartner an DATEV.');
        return;
      }

      setKontaktperson(kontakt);
    } catch (err: any) {
      setKontaktperson(null);
      setKontaktFehler('Ansprechpartner konnte nicht ermittelt werden. Die Anfrage geht ohne Ansprechpartner an DATEV.');
    }
  }, []);

  useEffect(() => { loadKontaktperson(); }, [loadKontaktperson]);

  // ============================================
  // MITARBEITERSPIEGEL LADEN
  // ============================================

  const loadSpiegel = useCallback(async () => {
    setSpiegelLoading(true);
    setSpiegelFehler(null);
    try {
      const { data, error } = await supabase
        .from('datev_employees')
        .select('personalnummer, vorname, nachname, betriebliche_nummer:raw_data->>company_personnel_number, employment_periods:raw_data->employment_periods, social_insurance:raw_data->social_insurance')
        .eq('customer_id', customerId)
        .limit(100000);

      if (error) {
        setSpiegel([]);
        setSpiegelFehler('Mitarbeiterspiegel konnte nicht geladen werden. Personalnummer bitte manuell erfassen.');
        return;
      }

      const zeilen: SpiegelMitarbeiter[] = (data || []).map((row: any) => {
        const perioden = Array.isArray(row.employment_periods) ? row.employment_periods : [];
        const letzte = perioden.length > 0 ? perioden[perioden.length - 1] : null;
        const sv = row.social_insurance || {};
        const kv = sv.contribution_class_health_insurance;
        return {
          personalnummer: String(row.personalnummer),
          vorname: row.vorname,
          nachname: row.nachname,
          betrieblicheNummer: row.betriebliche_nummer ? String(row.betriebliche_nummer) : null,
          eintritt: letzte?.date_of_commencement_of_employment || null,
          austritt: letzte?.date_of_termination_of_employment || null,
          kvBeitragsgruppe: typeof kv === 'number' ? kv : null
        };
      });

      zeilen.sort((a, b) => {
        const nameA = `${a.nachname || ''} ${a.vorname || ''}`.trim().toLowerCase();
        const nameB = `${b.nachname || ''} ${b.vorname || ''}`.trim().toLowerCase();
        return nameA.localeCompare(nameB, 'de');
      });

      setSpiegel(zeilen);
    } finally {
      setSpiegelLoading(false);
    }
  }, [customerId]);

  useEffect(() => { loadSpiegel(); }, [loadSpiegel]);

  // ============================================
  // HISTORIE LADEN
  // ============================================

  const loadHistory = useCallback(async () => {
    // Startzeit merken, um den Spinner minimal sichtbar zu halten (siehe unten).
    const started = Date.now();
    setHistoryLoading(true);
    try {
      const { data, error } = await supabase
        .from('datev_eau_requests')
        .select('*')
        .eq('customer_id', customerId)
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) {
        console.error('Historie-Fehler:', error);
        setHistory([]);
      } else {
        setHistory((data as EauHistoryRow[]) || []);
        setLastRefreshedAt(new Date());
      }
    } finally {
      // Die lokale Abfrage ist oft in wenigen Millisekunden fertig — dann bliebe
      // der Spinner unsichtbar und der Klick wirkte wirkungslos. Deshalb halten
      // wir den Ladezustand mindestens ~500ms sichtbar.
      const elapsed = Date.now() - started;
      const minVisibleMs = 500;
      if (elapsed < minVisibleMs) {
        await new Promise(resolve => setTimeout(resolve, minVisibleMs - elapsed));
      }
      setHistoryLoading(false);
    }
  }, [customerId, clientId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // ============================================
  // ANLAGE-KORB
  // ============================================

  const resetCreateForm = () => {
    setKorb([]);
    setSuchbegriff('');
    setManuellePnr('');
    setSammelDatum('');
    setNewEmail(XTAX_EAU_BENACHRICHTIGUNG_EMAIL);
    setComplianceChecked(false);
    setCreateError(null);
  };

  const mitarbeiterName = (m: SpiegelMitarbeiter): string => {
    const name = `${m.nachname || ''}, ${m.vorname || ''}`.replace(/^, |, $/g, '').trim();
    return name || `Personalnummer ${m.personalnummer}`;
  };

  const addAusSpiegel = (m: SpiegelMitarbeiter) => {
    setCreateError(null);
    setKorb(prev => {
      const key = `spiegel-${m.personalnummer}-${Date.now()}`;
      return [...prev, {
        key,
        personalnummer: m.personalnummer,
        name: mitarbeiterName(m),
        betrieblicheNummer: m.betrieblicheNummer,
        eintritt: m.eintritt,
        austritt: m.austritt,
        kvBeitragsgruppe: m.kvBeitragsgruppe,
        manuell: false,
        auBeginn: sammelDatum,
        auEnde: '',
        folgebescheinigung: false,
        ergebnis: 'offen',
        ergebnisText: null,
        eauUuid: null
      }];
    });
  };

  const addManuell = () => {
    const pnr = manuellePnr.trim();
    if (!pnr) return;
    setCreateError(null);
    const treffer = spiegel.find(m => m.personalnummer === pnr);
    if (treffer) {
      addAusSpiegel(treffer);
    } else {
      setKorb(prev => [...prev, {
        key: `manuell-${pnr}-${Date.now()}`,
        personalnummer: pnr,
        name: `Personalnummer ${pnr}`,
        betrieblicheNummer: null,
        eintritt: null,
        austritt: null,
        kvBeitragsgruppe: null,
        manuell: true,
        auBeginn: sammelDatum,
        auEnde: '',
        folgebescheinigung: false,
        ergebnis: 'offen',
        ergebnisText: null,
        eauUuid: null
      }]);
    }
    setManuellePnr('');
  };

  const removeZeile = (key: string) => {
    setKorb(prev => prev.filter(z => z.key !== key));
  };

  const setZeileDatum = (key: string, wert: string) => {
    setKorb(prev => prev.map(z => z.key === key ? { ...z, auBeginn: wert } : z));
  };

  const setZeileEnde = (key: string, wert: string) => {
    setKorb(prev => prev.map(z => z.key === key ? { ...z, auEnde: wert } : z));
  };

  const setZeileFolge = (key: string, wert: boolean) => {
    setKorb(prev => prev.map(z => z.key === key ? { ...z, folgebescheinigung: wert } : z));
  };

  const datumFuerAlleUebernehmen = () => {
    if (!sammelDatum) return;
    setKorb(prev => prev.map(z => z.ergebnis === 'erfolg' ? z : { ...z, auBeginn: sammelDatum }));
  };

  // ============================================
  // VORPRUEFUNGEN
  // ============================================
  //
  // Die Hinweise blockieren den Versand nicht. Sie greifen den Fehlermeldungen
  // vor, die DATEV bei unplausiblen Angaben liefert, und machen sie vorab
  // sichtbar. Einzige harte Bedingung ist ein gesetztes AU-Beginn-Datum.

  const heuteIso = (): string => {
    const jetzt = new Date();
    const monat = String(jetzt.getMonth() + 1).padStart(2, '0');
    const tag = String(jetzt.getDate()).padStart(2, '0');
    return `${jetzt.getFullYear()}-${monat}-${tag}`;
  };

  const zeileHinweise = (z: AnlageZeile): string[] => {
    const hinweise: string[] = [];
    if (!z.auBeginn) {
      hinweise.push('Beginn der Arbeitsunfähigkeit fehlt.');
      return hinweise;
    }
    if (z.auBeginn > heuteIso()) {
      hinweise.push('Beginn liegt in der Zukunft. DATEV weist solche Anfragen ab.');
    }
    if (z.auBeginn < '2021-10-01') {
      hinweise.push('Beginn liegt vor dem 01.10.2021. Das eAU-Verfahren gilt erst ab diesem Datum.');
    }
    // Das Ende geht nicht an DATEV, sondern dient dem Abgleich gegen die
    // Rückmeldung. Ohne Ende bliebe unbemerkt, wenn die Kasse nur einen
    // Teilzeitraum bestätigt.
    if (!z.auEnde) {
      hinweise.push('Ende des Krankheitszeitraums fehlt. Ohne diese Angabe lässt sich nicht prüfen, ob die Kasse den ganzen Zeitraum bestätigt.');
    } else if (z.auEnde < z.auBeginn) {
      hinweise.push('Ende liegt vor dem Beginn.');
    }
    if (z.eintritt && z.auBeginn < z.eintritt) {
      hinweise.push(`Beginn liegt vor dem Eintritt am ${formatShortDate(z.eintritt)}.`);
    }
    if (z.austritt && z.auBeginn > z.austritt) {
      hinweise.push(`Beginn liegt nach dem Austritt am ${formatShortDate(z.austritt)}.`);
    }
    if (z.kvBeitragsgruppe === 0) {
      hinweise.push('Keine gesetzliche Krankenversicherung erkennbar (KV-Beitragsgruppe 0). Für privat Versicherte ist kein eAU-Abruf zulässig.');
    }
    if (z.manuell) {
      hinweise.push('Personalnummer manuell erfasst, im Spiegel nicht gefunden. Keine Vorprüfung möglich.');
    }
    return hinweise;
  };

  const versandbereit = korb.length > 0
    && korb.some(z => z.ergebnis !== 'erfolg')
    && korb.filter(z => z.ergebnis !== 'erfolg')
        .every(z => !!z.auBeginn && !!z.auEnde && z.auEnde >= z.auBeginn)
    && complianceChecked;

  // ============================================
  // eAU-ANFRAGEN SENDEN
  // ============================================

  const sendKorb = async () => {
    setCreateError(null);

    if (!complianceChecked) {
      setCreateError('Bitte bestätigen Sie den Hinweis nach §5 EntgFG, bevor Sie die eAU anfragen.');
      return;
    }
    const offene = korb.filter(z => z.ergebnis !== 'erfolg');
    if (offene.length === 0) {
      setCreateError('Bitte wählen Sie mindestens einen Mitarbeiter aus.');
      return;
    }
    if (offene.some(z => !z.auBeginn)) {
      setCreateError('Bei jedem Mitarbeiter muss der Beginn der Arbeitsunfähigkeit gesetzt sein.');
      return;
    }
    if (offene.some(z => !z.auEnde)) {
      setCreateError('Bei jedem Mitarbeiter muss das Ende des Krankheitszeitraums gesetzt sein.');
      return;
    }
    if (offene.some(z => z.auEnde < z.auBeginn)) {
      setCreateError('Das Ende des Krankheitszeitraums darf nicht vor dem Beginn liegen.');
      return;
    }

    setCreating(true);
    try {
      for (const zeile of offene) {
        try {
          // Zuerst den Zeitraum bei uns festhalten, dann anfragen.
          //
          // Der eAU-Anforderungsdatensatz kennt kein Enddatum, die Kasse
          // antwortet deshalb immer nur mit dem AU-Zeitraum rund um das
          // angefragte Startdatum. Ohne hinterlegten Zeitraum gälte der
          // Vorgang als erledigt, sobald irgendetwas bestätigt wird, auch
          // wenn das nur ein Teil des Krankheitszeitraums ist.
          //
          // Mit dem Befund greift dieselbe Mechanik wie bei den Zeiträumen
          // aus dem Fehlzeitenabgleich: nächtliche Deckungsprüfung und
          // selbständiges Nachfassen, bis der Zeitraum gedeckt ist.
          //
          // Existiert zum selben Beginn bereits ein Zeitraum, wird sein Ende
          // nur verlängert, nie verkürzt. Ein vom Kalendarium gemeldeter
          // längerer Zeitraum bleibt damit erhalten.
          let befundId: string | null = null;
          try {
            const { data: vorhanden } = await supabase
              .from('datev_absence_findings')
              .select('id, ende')
              .eq('client_id', clientId)
              .eq('personnel_number', zeile.personalnummer)
              .eq('beginn', zeile.auBeginn)
              .maybeSingle();

            if (vorhanden?.id) {
              befundId = vorhanden.id;
              if (!vorhanden.ende || zeile.auEnde > vorhanden.ende) {
                await supabase
                  .from('datev_absence_findings')
                  .update({ ende: zeile.auEnde })
                  .eq('id', vorhanden.id);
              }
            } else {
              const { data: neu } = await supabase
                .from('datev_absence_findings')
                .insert({
                  customer_id: customerId,
                  client_id: clientId,
                  personnel_number: zeile.personalnummer,
                  beginn: zeile.auBeginn,
                  ende: zeile.auEnde,
                  grund_schluessel: [],
                  tage_anzahl: 0,
                  abgleich_status: 'offen',
                  herkunft: 'manuell',
                  notiz: `Zeitraum bei der Anfrage im Intranet erfasst${angemeldeteEmail ? ` von ${angemeldeteEmail}` : ''}`
                })
                .select('id')
                .single();
              befundId = neu?.id ?? null;
            }
          } catch (befundFehler) {
            // Der Zeitraum konnte nicht hinterlegt werden. Die Anfrage geht
            // trotzdem raus, denn sie ist das Dringendere. Ohne Zeitraum
            // entfällt allerdings die spätere Deckungsprüfung, deshalb wird
            // das unten am Ergebnis vermerkt.
            console.error('Zeitraum konnte nicht hinterlegt werden:', befundFehler);
            befundId = null;
          }

          const response = await datevEauService.createRequest(
            { customerId, clientId, retrievedBy: angemeldeteEmail || undefined },
            zeile.personalnummer,
            {
              source: newSource,
              start_work_incapacity: zeile.auBeginn,
              notification: newEmail ? { email: newEmail } : undefined,
              contact_person: kontaktperson || undefined,
              follow_up_certification: zeile.folgebescheinigung,
              __compliance_confirmed: true,
              __absence_finding_id: befundId || undefined
            }
          );

          // Gegenrichtung der Verknüpfung setzen. Die Route hält den Bezug
          // bereits im Vorgang fest; im Zeitraum fehlt er noch, und daran
          // hängt die bestehende Abgleichslogik.
          if (befundId) {
            try {
              const { data: vorgang } = await supabase
                .from('datev_eau_requests')
                .select('id')
                .eq('client_id', clientId)
                .eq('personnel_number', zeile.personalnummer)
                .eq('start_work_incapacity', zeile.auBeginn)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

              await supabase
                .from('datev_absence_findings')
                .update({
                  abgleich_status: 'vorhanden',
                  eau_request_id: vorgang?.id ?? null,
                  erledigt_am: new Date().toISOString()
                })
                .eq('id', befundId);
            } catch (verknuepfFehler) {
              console.error('Vorgang konnte nicht mit dem Zeitraum verknüpft werden:', verknuepfFehler);
            }
          }

          setKorb(prev => prev.map(z => z.key === zeile.key
            ? {
                ...z,
                ergebnis: 'erfolg',
                ergebnisText: befundId
                  ? 'Anfrage gesendet, Zeitraum wird überwacht'
                  : 'Anfrage gesendet, Zeitraum konnte nicht hinterlegt werden',
                eauUuid: response.eau_uuid
              }
            : z));
        } catch (err: any) {
          const details: string[] = [];
          if (err.error) details.push(err.error);
          if (Array.isArray(err.additional_messages)) {
            err.additional_messages.forEach((msg: any) => {
              if (msg?.description) details.push(`${msg.id || ''} ${msg.description}`.trim());
            });
          }
          setKorb(prev => prev.map(z => z.key === zeile.key
            ? { ...z, ergebnis: 'fehler', ergebnisText: details.join(' | ') || 'Anfrage fehlgeschlagen' }
            : z));
        }
      }
      await loadHistory();
    } finally {
      setCreating(false);
    }
  };

  // ============================================
  // FEEDBACK ABRUFEN
  // ============================================

  const fetchFeedback = async (row: EauHistoryRow) => {
    setActionUuid(row.eau_uuid);
    setActionType('feedback');
    try {
      await datevEauService.getFeedback({ customerId, clientId }, row.personnel_number, row.eau_uuid);
      await loadHistory();
    } catch (err: any) {
      console.error('Feedback-Abruf fehlgeschlagen:', err);
      alert(`Feedback-Abruf fehlgeschlagen: ${err.error || 'Unbekannter Fehler'}`);
    } finally {
      setActionUuid(null);
      setActionType(null);
    }
  };

  // ============================================
  // eAU STORNIEREN
  // ============================================

  const cancelEau = async (row: EauHistoryRow) => {
    if (!window.confirm(`eAU-Anfrage ${row.eau_uuid} für Personalnummer ${row.personnel_number} wirklich stornieren?`)) return;
    setActionUuid(row.eau_uuid);
    setActionType('cancel');
    try {
      await datevEauService.cancelRequest({ customerId, clientId }, row.personnel_number, row.eau_uuid);
      await loadHistory();
    } catch (err: any) {
      console.error('Stornierung fehlgeschlagen:', err);
      alert(`Stornierung fehlgeschlagen: ${err.error || 'Unbekannter Fehler'}`);
    } finally {
      setActionUuid(null);
      setActionType(null);
    }
  };

  // ============================================
  // RENDER-HELFER
  // ============================================

  const inputStyle: React.CSSProperties = {
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: 'white',
    width: '100%',
    boxSizing: 'border-box'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '4px'
  };

  const formatDate = (iso: string | null): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  function formatShortDate(iso: string | null): string {
    if (!iso) return '—';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    return iso;
  }

  const formatJaNein = (wert: boolean | undefined): string => {
    if (wert === undefined || wert === null) return '—';
    return wert ? 'Ja' : 'Nein';
  };

  // ============================================
  // ABLEHNUNGEN IN KLARTEXT
  // ============================================
  //
  // DATEV liefert bei einer abgelehnten Anfrage einen Schlüssel und einen
  // englischen Text. Für die tägliche Arbeit ist beides unbrauchbar. Wichtig ist
  // nur, ob der Fall damit erledigt ist oder ob jemand etwas tun muss.
  //
  // Ist ein Mitarbeiter privat versichert, gibt es keinen eAU-Weg. Das ist kein
  // Fehler, sondern das endgültige Ergebnis: Der Nachweis kommt auf Papier.
  // Solche Fälle werden deshalb wie eine abgeschlossene Rückmeldung dargestellt.
  interface AblehnungInfo {
    /** Der Vorgang ist fachlich erledigt, es ist nichts weiter zu tun. */
    erledigt: boolean;
    /** Kurzer Klartext für die Zeile. */
    hinweis: string;
    bg: string;
    fg: string;
  }

  const ablehnungInfo = (code: string | null): AblehnungInfo => {
    switch (code) {
      case 'EAU C-PL-0000001':
        return {
          erledigt: true,
          hinweis: 'Privat versichert, eAU nicht möglich',
          bg: '#FEF3C7', fg: '#92400E'
        };
      case 'EAU C-RE-0000000':
        return {
          erledigt: true,
          hinweis: 'Bereits angefragt, Sperrfrist von fünf Tagen',
          bg: '#FEF3C7', fg: '#92400E'
        };
      case 'EAU C-PL-0000002':
        return {
          erledigt: false,
          hinweis: 'Kein Beschäftigungsverhältnis zum AU-Beginn',
          bg: '#FEE2E2', fg: '#991B1B'
        };
      case 'EAU C-PL-0000003':
        return {
          erledigt: false,
          hinweis: 'Keine passenden Versicherungsdaten zum AU-Beginn',
          bg: '#FEE2E2', fg: '#991B1B'
        };
      case 'EAU C-PL-0000000':
        return {
          erledigt: false,
          hinweis: 'Mitarbeiter im DATEV-Lohnsystem nicht gefunden',
          bg: '#FEE2E2', fg: '#991B1B'
        };
      case 'EAU C-CL-0000000':
        return {
          erledigt: false,
          hinweis: 'Mandant im DATEV-Lohnsystem nicht für eAU freigeschaltet',
          bg: '#FEE2E2', fg: '#991B1B'
        };
      case 'EAU C-PL-0000005':
      case 'EAU C-PL-0000006':
        return {
          erledigt: false,
          hinweis: 'Beginn der Arbeitsunfähigkeit unzulässig',
          bg: '#FEE2E2', fg: '#991B1B'
        };
      default:
        return {
          erledigt: false,
          hinweis: 'Von DATEV abgelehnt',
          bg: '#FEE2E2', fg: '#991B1B'
        };
    }
  };

  const renderFlagBadge = (flag: DatevEauFlag | undefined) => {
    if (!flag) return <span style={{ fontSize: '12px', color: '#6B7280' }}>—</span>;
    const label = DATEV_EAU_FLAG_LABELS[flag] || `Flag ${flag}`;
    const colors = DATEV_EAU_FLAG_COLORS[flag] || { bg: '#E5E7EB', fg: '#374151' };
    return (
      <span style={{
        display: 'inline-block',
        backgroundColor: colors.bg,
        color: colors.fg,
        fontSize: '12px',
        fontWeight: '500',
        padding: '3px 8px',
        borderRadius: '4px'
      }}>{label}</span>
    );
  };

  const renderStateBadge = (state: string, ablehnungCode?: string | null) => {
    let bg = '#DBEAFE', fg = '#1E40AF', label = state;
    if (state === 'requested') { label = 'Anfrage gesendet'; }
    if (state === 'feedback_received') { bg = '#D1FAE5'; fg = '#065F46'; label = 'Rückmeldung erhalten'; }
    if (state === 'zwischenmeldung') { bg = '#FEF3C7'; fg = '#92400E'; label = 'Zwischenmeldung, Kasse liefert nach'; }
    if (state === 'abgeschlossen') { bg = '#D1FAE5'; fg = '#065F46'; label = 'Abgeschlossen'; }
    if (state === 'abgelehnt') {
      // Eine Ablehnung, gegen die nichts zu unternehmen ist, ist für die
      // tägliche Arbeit ein abgeschlossener Vorgang und wird auch so angezeigt.
      // Der Grund steht daneben im eigenen Feld.
      const info = ablehnungInfo(ablehnungCode ?? null);
      if (info.erledigt) { bg = '#D1FAE5'; fg = '#065F46'; label = 'Abgeschlossen'; }
      else { bg = '#FEE2E2'; fg = '#991B1B'; label = 'Von DATEV abgelehnt'; }
    }
    if (state === 'cancelled') { bg = '#E5E7EB'; fg = '#374151'; label = 'Storniert'; }
    if (state === 'error') { bg = '#FEE2E2'; fg = '#991B1B'; label = 'Fehler'; }
    return (
      <span style={{
        display: 'inline-block',
        backgroundColor: bg,
        color: fg,
        fontSize: '12px',
        fontWeight: '500',
        padding: '3px 8px',
        borderRadius: '4px'
      }}>{label}</span>
    );
  };

  /** Kennzeichnung des Ablehnungsgrundes, im selben Format wie das Kassenkennzeichen. */
  const renderAblehnungBadge = (code: string | null) => {
    const info = ablehnungInfo(code);
    return (
      <span style={{
        display: 'inline-block',
        backgroundColor: info.bg,
        color: info.fg,
        fontSize: '12px',
        fontWeight: '500',
        padding: '3px 8px',
        borderRadius: '4px'
      }}>{info.hinweis}</span>
    );
  };

  const renderFeedbackDetail = (feedback: DatevEauFeedback | null) => {
    if (!feedback) return null;
    const fbs = feedback.feedbacks_from_health_insurance || [];
    if (fbs.length === 0) {
      return (
        <div style={{ padding: '16px', fontSize: '13px', color: '#6B7280', fontStyle: 'italic' }}>
          Keine Rückmeldung von der Krankenkasse vorhanden.
        </div>
      );
    }

    return fbs.map((fb, idx) => {
      const iw = fb.incapacity_for_work;
      const cp = fb.contact_person;
      const errs = fb.error_block_list;

      return (
        <div key={idx} style={{
          padding: '16px 20px',
          backgroundColor: '#F9FAFB',
          borderRadius: '6px',
          marginBottom: fbs.length > 1 ? '12px' : 0
        }}>
          {iw && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                Arbeitsunfähigkeit
              </div>
              <div style={{ marginBottom: '10px' }}>
                {renderFlagBadge(iw.flag_current_work_incapacity)}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Beginn AU (laut Arbeitgeber)</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {formatShortDate(iw.start_work_incapacity_employer)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Beginn AU (laut Bescheinigung)</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {formatShortDate(iw.start_work_incapacity_au || null)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Ende AU (erwartet)</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {formatShortDate(iw.end_work_incapacity_au || null)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Ende AU (tatsächlich)</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {formatShortDate(iw.actual_end_work_incapacity_au || null)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Diagnosedatum</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {formatShortDate(iw.date_of_diagnosis || null)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Erstbescheinigung</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {iw.initial_certificate === undefined ? '—' : iw.initial_certificate ? 'Ja' : 'Folgebescheinigung'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Arbeitsunfall</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {formatJaNein(iw.accident_at_work)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Zuweisung Durchgangsarzt</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {formatJaNein(iw.assignment_accident_insurance_doctor)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Sonstiger Unfall</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {formatJaNein(iw.other_accident)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Beginn Krankenhausaufenthalt</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {formatShortDate(iw.start_hospitalisation || null)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Ende Krankenhausaufenthalt</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {formatShortDate(iw.end_hospitalisation || null)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Automatische Rückmeldung bis</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {formatShortDate(iw.automatic_feedback_until || null)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {cp && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                Krankenkasse
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Krankenkasse</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{cp.name1_health_insurance}</div>
                </div>
                {(cp.name2_health_insurance || cp.name3_health_insurance) && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#6B7280' }}>Namenszusatz</div>
                    <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                      {[cp.name2_health_insurance, cp.name3_health_insurance].filter(Boolean).join(' ')}
                    </div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Ansprechpartner</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{cp.name}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Telefon</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{cp.telephone || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Fax</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{cp.fax || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>E-Mail</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{cp.email || '—'}</div>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Adresse</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                    {cp.street || ''} {cp.house_number || ''}, {cp.postal_code} {cp.city}
                  </div>
                </div>
              </div>
            </div>
          )}

          {errs && errs.length > 0 && (
            <div>
              <div style={{ fontSize: '12px', color: '#991B1B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                Fehlermeldungen
              </div>
              {errs.map((err, i) => (
                <div key={i} style={{
                  backgroundColor: '#FEE2E2',
                  border: '1px solid #FECACA',
                  borderRadius: '4px',
                  padding: '8px 10px',
                  marginBottom: '6px',
                  fontSize: '12px'
                }}>
                  <div style={{ color: '#991B1B' }}>
                    <strong>{err.origin === 'EXTERN' ? 'Krankenkasse' : 'DATEV'}</strong>
                    {err.error_number && <span style={{ fontFamily: 'Menlo, monospace', marginLeft: '8px', fontSize: '11px' }}>{err.error_number}</span>}
                  </div>
                  {err.error_text && <div style={{ marginTop: '4px', color: '#374151' }}>{err.error_text}</div>}
                  {err.error_value && <div style={{ marginTop: '2px', color: '#6B7280', fontSize: '11px' }}>Wert: {err.error_value}</div>}
                </div>
              ))}
            </div>
          )}

          <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '12px', fontFamily: 'Menlo, monospace' }}>
            GUID: {fb.guid}
          </div>
        </div>
      );
    });
  };

  // ============================================
  // MITARBEITERSUCHE: TREFFERLISTE
  // ============================================

  const suchtreffer = (): SpiegelMitarbeiter[] => {
    const begriff = suchbegriff.trim().toLowerCase();
    if (begriff.length < 2) return [];
    return spiegel.filter(m => {
      if (m.personalnummer.toLowerCase().includes(begriff)) return true;
      if (m.betrieblicheNummer && m.betrieblicheNummer.toLowerCase().includes(begriff)) return true;
      const name = `${m.vorname || ''} ${m.nachname || ''}`.toLowerCase();
      return name.includes(begriff);
    }).slice(0, 20);
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div>
      {/* Header */}
      <div style={{
        backgroundColor: '#EFF6FF',
        border: '1px solid #DBEAFE',
        borderRadius: '6px',
        padding: '12px',
        marginBottom: '20px',
        fontSize: '14px',
        color: '#1E40AF'
      }}>
        <strong>Elektronische Arbeitsunfähigkeitsbescheinigung (hr:eau):</strong> Anfrage der eAU bei der
        zuständigen Krankenkasse für Bestand <strong>{clientId}</strong>.
      </div>

      {/* Berechtigungsprüfung-Status */}
      {!accessChecked && (
        <div style={{ padding: '24px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
          <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: '8px' }} />
          Berechtigung wird geprüft...
        </div>
      )}

      {accessChecked && !hasAccess && (
        <div style={{
          backgroundColor: '#FEE2E2',
          border: '1px solid #FECACA',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '16px',
          fontSize: '14px',
          color: '#991B1B',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px'
        }}>
          <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong>Keine Berechtigung für Bestand {clientId}.</strong>
            <div style={{ marginTop: '4px' }}>{accessError}</div>
          </div>
        </div>
      )}

      {/* Audit-Auflage 2: Freigegebene Mandanten bei Berechtigungsfehler */}
      {availableClients && availableClients.length > 0 && !hasAccess && accessChecked && (
        <div style={{
          backgroundColor: '#FEF3C7',
          border: '1px solid #FDE68A',
          borderRadius: '6px',
          padding: '14px',
          marginBottom: '16px',
          fontSize: '13px',
          color: '#78350F'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '8px' }}>
            Für diesen Scope freigegebene Mandanten:
          </div>
          {availableClients.map((group, idx) => (
            <div key={idx} style={{ marginBottom: '6px' }}>
              <span style={{ fontWeight: '500' }}>Beraternummer {group.consultant}:</span>{' '}
              Mandanten {group.ranges.join(', ')}
              <span style={{ color: '#92400E', fontSize: '12px', marginLeft: '8px' }}>
                ({group.count} Mandanten)
              </span>
            </div>
          ))}
          <div style={{ marginTop: '10px', fontStyle: 'italic', color: '#92400E', fontSize: '12px' }}>
            Wenden Sie sich an Ihren Steuerberater, falls Mandanten fehlen.
          </div>
        </div>
      )}

      {accessChecked && hasAccess && (
        <div style={{
          backgroundColor: '#F0FDF4',
          border: '1px solid #BBF7D0',
          borderRadius: '6px',
          padding: '10px 12px',
          marginBottom: '12px',
          fontSize: '13px',
          color: '#166534',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <CheckCircle size={16} />
          <span>Berechtigung für Bestand {clientId} bestätigt (HTTP 200)</span>
        </div>
      )}

      {/* Berechtigungs-Test, eingeklappt hinter Info-Symbol */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => setTestPanelOffen(!testPanelOffen)}
          title="Nachweis für die DATEV-Abnahme: Verhalten bei ungültiger Berater-/Mandantennummer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: 'none', border: 'none', padding: 0,
            color: '#6B7280', fontSize: '12px', cursor: 'pointer'
          }}
        >
          <Info size={14} />
          {testPanelOffen ? 'Berechtigungsprüfung testen ausblenden' : 'Berechtigungsprüfung testen'}
        </button>

        {testPanelOffen && (
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '16px 20px',
            marginTop: '10px'
          }}>
            <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>
              DATEV verlangt Nachweis, dass bei ungültiger Berater-/Mandantennummer eine Fehlermeldung erscheint.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', alignItems: 'flex-end' }}>
              <div>
                <label style={labelStyle}>Beraternummer</label>
                <input type="text" value={testBerater} onChange={e => setTestBerater(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Mandantennummer</label>
                <input type="text" value={testMandant} onChange={e => setTestMandant(e.target.value)} style={inputStyle} />
              </div>
              <button
                onClick={runAccessTest}
                disabled={testRunning || !testBerater || !testMandant}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px',
                  backgroundColor: testRunning ? '#9CA3AF' : '#3B82F6',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: testRunning ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  whiteSpace: 'nowrap'
                }}
              >
                <Search size={14} />
                Prüfen
              </button>
            </div>

            {testResult === 'success' && (
              <div style={{
                marginTop: '12px',
                backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0',
                borderRadius: '6px', padding: '10px 12px',
                fontSize: '13px', color: '#166534',
                display: 'flex', alignItems: 'center', gap: '8px'
              }}>
                <CheckCircle size={16} />
                <span><strong>HTTP {testStatus}:</strong> Berechtigung für {testBerater}-{testMandant} bestätigt.</span>
              </div>
            )}

            {testResult === 'failure' && (
              <div style={{
                marginTop: '12px',
                backgroundColor: '#FEE2E2', border: '1px solid #FECACA',
                borderRadius: '6px', padding: '10px 12px',
                fontSize: '13px', color: '#991B1B',
                display: 'flex', alignItems: 'flex-start', gap: '8px'
              }}>
                <XCircle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                <div>
                  <strong>HTTP {testStatus}:</strong> Keine Berechtigung für {testBerater}-{testMandant}.
                  <div style={{ marginTop: '2px', fontSize: '12px' }}>{testError}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {accessChecked && hasAccess && (
        <>
          {/* Neue Anfrage: Button oder Formular */}
          {view === 'overview' && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
              <button
                onClick={() => { resetCreateForm(); setView('create'); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '10px 16px',
                  backgroundColor: '#10B981',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: 'pointer', fontSize: '14px', fontWeight: '500'
                }}
              >
                <Plus size={16} />
                Neue eAU-Anfrage
              </button>
            </div>
          )}

          {view === 'create' && (
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>Neue eAU-Anfrage</h3>
                <button
                  onClick={() => { resetCreateForm(); setView('overview'); }}
                  style={{ background: 'none', border: 'none', color: '#6B7280', fontSize: '13px', cursor: 'pointer', padding: 0 }}
                >
                  Abbrechen
                </button>
              </div>

              {/* §5 EntgFG Hinweis */}
              <div style={{
                backgroundColor: '#FEF3C7',
                border: '1px solid #FDE68A',
                borderRadius: '6px',
                padding: '12px 14px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#78350F'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: '1px', color: '#D97706' }} />
                  <div>
                    <strong>Rechtlicher Hinweis (§5 Abs. 1 EntgFG):</strong>
                    <div style={{ marginTop: '4px' }}>
                      Eine eAU-Abfrage darf nur durchgeführt werden, wenn sich der Arbeitnehmer gemäß §5 Abs. 1
                      Entgeltfortzahlungsgesetz bei seinem Arbeitgeber krankgemeldet hat. Die Abfrage sollte frühestens
                      ab dem 4. Tag nach der Krankmeldung erfolgen.
                    </div>
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      marginTop: '10px', cursor: 'pointer',
                      fontWeight: '500', color: '#78350F'
                    }}>
                      <input
                        type="checkbox"
                        checked={complianceChecked}
                        onChange={e => setComplianceChecked(e.target.checked)}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      <span>Ich bestätige, dass der Arbeitnehmer sich gemäß §5 Abs. 1 EntgFG krankgemeldet hat.</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Gemeinsame Angaben */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '12px', alignItems: 'flex-end', marginBottom: '16px' }}>
                <div>
                  <label style={labelStyle}>Quell-System *</label>
                  <select value={newSource} onChange={e => setNewSource(e.target.value as DatevEauSource)} style={inputStyle}>
                    <option value="LUG">Lohn und Gehalt (LUG)</option>
                    <option value="LODAS">LODAS</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Benachrichtigungs-E-Mail</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    placeholder={XTAX_EAU_BENACHRICHTIGUNG_EMAIL}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Beginn Arbeitsunfähigkeit</label>
                  <input
                    type="date"
                    value={sammelDatum}
                    onChange={e => setSammelDatum(e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <button
                  onClick={datumFuerAlleUebernehmen}
                  disabled={!sammelDatum || korb.length === 0}
                  title="Dieses Datum bei allen ausgewählten Mitarbeitern setzen"
                  style={{
                    padding: '8px 14px',
                    backgroundColor: 'white', border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    cursor: (!sammelDatum || korb.length === 0) ? 'not-allowed' : 'pointer',
                    fontSize: '13px', color: '#374151', whiteSpace: 'nowrap'
                  }}
                >
                  Für alle setzen
                </button>
              </div>

              {/* Ansprechpartner-Hinweis */}
              {kontaktperson && (
                <div style={{
                  backgroundColor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  padding: '10px 12px',
                  marginBottom: '16px',
                  fontSize: '12px',
                  color: '#374151'
                }}>
                  <strong>Ansprechpartner für Rückfragen der Krankenkasse:</strong>{' '}
                  {kontaktperson.name}, {kontaktperson.company_name}, {kontaktperson.street} {kontaktperson.house_number || ''}, {kontaktperson.postal_code} {kontaktperson.city},
                  Telefon {kontaktperson.telephone}, {kontaktperson.email}
                </div>
              )}
              {kontaktFehler && (
                <div style={{
                  backgroundColor: '#FEF3C7',
                  border: '1px solid #FDE68A',
                  borderRadius: '6px',
                  padding: '10px 12px',
                  marginBottom: '16px',
                  fontSize: '12px',
                  color: '#78350F',
                  display: 'flex', alignItems: 'flex-start', gap: '8px'
                }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
                  <span>{kontaktFehler}</span>
                </div>
              )}

              {/* Mitarbeitersuche */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Mitarbeiter suchen (Name, Personalnummer oder betriebliche Nummer)</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={suchbegriff}
                    onChange={e => setSuchbegriff(e.target.value)}
                    placeholder={spiegelLoading ? 'Mitarbeiterspiegel wird geladen...' : `Suche in ${spiegel.length} Mitarbeitern`}
                    style={inputStyle}
                  />
                </div>

                {spiegelFehler && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#92400E' }}>
                    {spiegelFehler}
                  </div>
                )}

                {suchbegriff.trim().length >= 2 && (
                  <div style={{
                    marginTop: '8px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    maxHeight: '220px',
                    overflowY: 'auto'
                  }}>
                    {suchtreffer().length === 0 && (
                      <div style={{ padding: '12px', fontSize: '13px', color: '#6B7280' }}>
                        Kein Mitarbeiter gefunden. Personalnummer unten manuell erfassen.
                      </div>
                    )}
                    {suchtreffer().map(m => (
                      <button
                        key={m.personalnummer}
                        onClick={() => addAusSpiegel(m)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '8px 12px',
                          background: 'none', border: 'none',
                          borderBottom: '1px solid #F3F4F6',
                          cursor: 'pointer', fontSize: '13px', color: '#111827'
                        }}
                      >
                        <span style={{ fontWeight: '600' }}>{mitarbeiterName(m)}</span>
                        <span style={{ color: '#6B7280', marginLeft: '8px' }}>
                          DATEV {m.personalnummer}
                          {m.betrieblicheNummer ? ` · betrieblich ${m.betrieblicheNummer}` : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginTop: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Personalnummer manuell erfassen</label>
                    <input
                      type="text"
                      value={manuellePnr}
                      onChange={e => setManuellePnr(e.target.value)}
                      placeholder="DATEV-Personalnummer"
                      style={inputStyle}
                    />
                  </div>
                  <button
                    onClick={addManuell}
                    disabled={!manuellePnr.trim()}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: 'white', border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      cursor: manuellePnr.trim() ? 'pointer' : 'not-allowed',
                      fontSize: '13px', color: '#374151', whiteSpace: 'nowrap'
                    }}
                  >
                    Hinzufügen
                  </button>
                </div>
              </div>

              {/* Auswahlkorb */}
              {korb.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                    Ausgewählte Mitarbeiter ({korb.length})
                  </div>
                  {korb.map(z => {
                    const hinweise = zeileHinweise(z);
                    return (
                      <div key={z.key} style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '6px',
                        padding: '10px 12px',
                        marginBottom: '8px',
                        backgroundColor: z.ergebnis === 'erfolg' ? '#F0FDF4' : z.ergebnis === 'fehler' ? '#FEF2F2' : 'white'
                      }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{z.name}</div>
                            <div style={{ fontSize: '11px', color: '#6B7280' }}>
                              DATEV {z.personalnummer}
                              {z.betrieblicheNummer ? ` · betrieblich ${z.betrieblicheNummer}` : ''}
                            </div>
                          </div>
                          <div style={{ width: '160px' }}>
                            <label style={labelStyle}>Beginn AU *</label>
                            <input
                              type="date"
                              value={z.auBeginn}
                              onChange={e => setZeileDatum(z.key, e.target.value)}
                              disabled={z.ergebnis === 'erfolg'}
                              style={inputStyle}
                            />
                          </div>
                          <div style={{ width: '160px' }}>
                            <label
                              style={labelStyle}
                              title="Wird nicht an DATEV gesendet. Der Anforderungsdatensatz kennt kein Enddatum. Die Angabe dient dem Abgleich gegen die Rückmeldung der Kasse."
                            >
                              Ende AU *
                            </label>
                            <input
                              type="date"
                              value={z.auEnde}
                              min={z.auBeginn || undefined}
                              onChange={e => setZeileEnde(z.key, e.target.value)}
                              disabled={z.ergebnis === 'erfolg'}
                              style={inputStyle}
                            />
                          </div>
                          <label style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            fontSize: '12px', color: '#374151', cursor: 'pointer', paddingBottom: '8px'
                          }}>
                            <input
                              type="checkbox"
                              checked={z.folgebescheinigung}
                              onChange={e => setZeileFolge(z.key, e.target.checked)}
                              disabled={z.ergebnis === 'erfolg'}
                              style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                            />
                            Folgebescheinigung
                          </label>
                          <button
                            onClick={() => removeZeile(z.key)}
                            title="Aus der Auswahl entfernen"
                            style={{
                              display: 'inline-flex', alignItems: 'center',
                              padding: '6px',
                              background: 'none', border: '1px solid #E5E7EB',
                              borderRadius: '4px', cursor: 'pointer', color: '#6B7280',
                              marginBottom: '6px'
                            }}
                          >
                            <X size={14} />
                          </button>
                        </div>

                        {hinweise.length > 0 && z.ergebnis !== 'erfolg' && (
                          <div style={{ marginTop: '8px' }}>
                            {hinweise.map((h, i) => (
                              <div key={i} style={{
                                display: 'flex', alignItems: 'flex-start', gap: '6px',
                                fontSize: '12px', color: '#92400E', marginTop: '2px'
                              }}>
                                <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: '2px' }} />
                                <span>{h}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {z.ergebnis === 'erfolg' && (
                          <div style={{ marginTop: '8px', fontSize: '12px', color: '#166534' }}>
                            <CheckCircle size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                            Anfrage gesendet
                            {z.eauUuid && (
                              <span style={{ fontFamily: 'Menlo, monospace', marginLeft: '8px', fontSize: '11px' }}>{z.eauUuid}</span>
                            )}
                          </div>
                        )}

                        {z.ergebnis === 'fehler' && (
                          <div style={{ marginTop: '8px', fontSize: '12px', color: '#991B1B' }}>
                            <XCircle size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                            {z.ergebnisText}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                onClick={sendKorb}
                disabled={creating || !versandbereit}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '10px 16px',
                  backgroundColor: (creating || !versandbereit) ? '#9CA3AF' : '#10B981',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: (creating || !versandbereit) ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '500'
                }}
              >
                {creating ? (
                  <>
                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Anfragen werden gesendet...
                  </>
                ) : (
                  <>
                    <Activity size={16} />
                    {korb.filter(z => z.ergebnis !== 'erfolg').length > 1
                      ? `${korb.filter(z => z.ergebnis !== 'erfolg').length} eAU-Anfragen senden`
                      : 'eAU-Anfrage senden'}
                  </>
                )}
              </button>

              {createError && (
                <div style={{
                  marginTop: '16px',
                  backgroundColor: '#FEE2E2',
                  border: '1px solid #FECACA',
                  borderRadius: '6px',
                  padding: '12px 14px',
                  fontSize: '13px',
                  color: '#991B1B',
                  display: 'flex', alignItems: 'center', gap: '8px'
                }}>
                  <XCircle size={16} />
                  <span>{createError}</span>
                </div>
              )}

              {korb.some(z => z.ergebnis === 'erfolg') && (
                <div style={{
                  marginTop: '16px',
                  backgroundColor: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  borderRadius: '6px',
                  padding: '12px 14px',
                  fontSize: '12px',
                  color: '#374151'
                }}>
                  Die Rückmeldung der Krankenkasse kann einige Tage dauern. DATEV benachrichtigt
                  {newEmail ? ` ${newEmail}` : ' die hinterlegte Adresse'}, sobald eine Rückmeldung vorliegt.
                  Der Abruf erfolgt anschliessend in der Anfrage-Historie.
                </div>
              )}
            </div>
          )}

          {/* Alle Anfragen dieses Mandanten mit Nachweisstand. Steht bewusst
              über der Historie: Hier steht, ob ein Zeitraum gedeckt ist,
              die Historie darunter ist die technische Vorgangsliste. */}
          <div style={{ marginBottom: '20px' }}>
            <EauUebersicht customerId={customerId} clientId={clientId} />
          </div>

          {/* Historie */}
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '16px 20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: '#111827' }}>
                Anfrage-Historie ({history.length})
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {lastRefreshedAt && (
                  <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
                    Zuletzt aktualisiert: {lastRefreshedAt.toLocaleTimeString('de-DE')}
                  </span>
                )}
                <button
                  onClick={ladeNachweis}
                  disabled={nachweisLaeuft || history.length === 0}
                  title="Übersicht aller Vorgänge dieses Mandanten als PDF herunterladen"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '6px 10px',
                    backgroundColor: 'white', border: '1px solid #D1D5DB',
                    borderRadius: '4px',
                    cursor: (nachweisLaeuft || history.length === 0) ? 'not-allowed' : 'pointer',
                    fontSize: '12px', color: '#374151',
                    opacity: history.length === 0 ? 0.5 : 1
                  }}
                >
                  {nachweisLaeuft
                    ? <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    : <Download size={12} />}
                  {nachweisLaeuft ? 'Wird erstellt...' : 'Übersicht als PDF'}
                </button>
                <button
                  onClick={loadHistory}
                  disabled={historyLoading}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '6px 10px',
                    backgroundColor: 'white', border: '1px solid #D1D5DB',
                    borderRadius: '4px', cursor: historyLoading ? 'not-allowed' : 'pointer',
                    fontSize: '12px', color: '#374151'
                  }}
                >
                  <RefreshCw size={12} style={historyLoading ? { animation: 'spin 1s linear infinite' } : undefined} />
                  {historyLoading ? 'Wird aktualisiert...' : 'Liste aktualisieren'}
                </button>
              </div>
            </div>

            {history.length === 0 && !historyLoading && (
              <div style={{ fontSize: '13px', color: '#6B7280', padding: '20px', textAlign: 'center' }}>
                Noch keine eAU-Anfragen vorhanden.
              </div>
            )}

            {history.map(row => {
              const isExpanded = detailRowId === row.id;
              const technikOffen = technikRowId === row.id;
              const busy = actionUuid === row.eau_uuid;
              const feedbackIw = row.last_feedback?.feedbacks_from_health_insurance?.[0]?.incapacity_for_work;
              const currentFlag = feedbackIw?.flag_current_work_incapacity as DatevEauFlag | undefined;
              const spiegelTreffer = spiegel.find(m => m.personalnummer === row.personnel_number);

              return (
                <div key={row.id} style={{
                  borderBottom: '1px solid #F3F4F6',
                  padding: '12px 0'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                          {spiegelTreffer ? mitarbeiterName(spiegelTreffer) : `PNr ${row.personnel_number}`}
                        </span>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>
                          DATEV {row.personnel_number}
                          {spiegelTreffer?.betrieblicheNummer ? ` · betrieblich ${spiegelTreffer.betrieblicheNummer}` : ''}
                        </span>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>
                          AU-Beginn: {formatShortDate(row.start_work_incapacity)}
                        </span>
                        <span style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase' }}>
                          {row.source}
                        </span>
                        {row.follow_up_certification && (
                          <span style={{
                            fontSize: '11px', padding: '1px 6px',
                            backgroundColor: '#E0E7FF', color: '#3730A3',
                            borderRadius: '3px', fontWeight: '500'
                          }}>Folge</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px', flexWrap: 'wrap' }}>
                        {renderStateBadge(row.state, row.ablehnung_code)}
                        {currentFlag && renderFlagBadge(currentFlag)}
                        {row.state === 'abgelehnt' && renderAblehnungBadge(row.ablehnung_code)}
                        <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
                          Angelegt: {formatDate(row.created_at)}
                        </span>
                        {row.last_feedback_fetched_at && (
                          <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
                            · Rückmeldung: {formatDate(row.last_feedback_fetched_at)}
                          </span>
                        )}
                      </div>
                      {row.eau_uuid && (
                        <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px', fontFamily: 'Menlo, monospace', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.eau_uuid}
                        </div>
                      )}

                      {/* Bei einer Ablehnung, gegen die nichts zu unternehmen ist, sagt
                          die Kennzeichnung oben bereits alles. Nur wenn jemand tätig
                          werden muss, steht hier der Hinweis dazu. Die Meldung von DATEV
                          im Originalwortlaut lässt sich bei Bedarf einblenden. */}
                      {row.state === 'abgelehnt' && !ablehnungInfo(row.ablehnung_code).erledigt && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#991B1B' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                            <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: '2px' }} />
                            <span>Diese Anfrage muss bearbeitet werden, DATEV hat sie nicht angenommen.</span>
                          </div>
                        </div>
                      )}

                      {row.state === 'abgelehnt' && (row.ablehnung_text || row.ablehnung_code) && (
                        <div style={{ marginTop: '6px' }}>
                          <button
                            onClick={() => setTechnikRowId(technikOffen ? null : row.id)}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              background: 'none', border: 'none', padding: 0,
                              color: '#9CA3AF', fontSize: '11px', cursor: 'pointer'
                            }}
                          >
                            <Info size={12} />
                            {technikOffen ? 'Meldung von DATEV ausblenden' : 'Meldung von DATEV anzeigen'}
                          </button>
                          {technikOffen && (
                            <div style={{
                              marginTop: '6px',
                              backgroundColor: '#F9FAFB',
                              border: '1px solid #E5E7EB',
                              borderRadius: '4px',
                              padding: '8px 10px',
                              fontSize: '11px',
                              color: '#6B7280'
                            }}>
                              {row.ablehnung_text && <div>{row.ablehnung_text}</div>}
                              {row.ablehnung_code && (
                                <div style={{ marginTop: '4px', fontFamily: 'Menlo, monospace' }}>
                                  {row.ablehnung_code}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                      {/* Abgelehnte Anfragen haben keine Vorgangs-UUID bei DATEV.
                          Rueckmeldung, Details und Storno sind dort gegenstandslos. */}
                      {row.state !== 'abgelehnt' && (
                        <>
                      <button
                        onClick={() => fetchFeedback(row)}
                        disabled={busy || row.state === 'cancelled'}
                        title="Rückmeldung der Krankenkasse abrufen"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '5px 10px',
                          backgroundColor: 'white', border: '1px solid #D1D5DB',
                          borderRadius: '4px',
                          cursor: (busy || row.state === 'cancelled') ? 'not-allowed' : 'pointer',
                          fontSize: '11px', color: '#374151'
                        }}
                      >
                        {busy && actionType === 'feedback' ? (
                          <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <FileText size={12} />
                        )}
                        Rückmeldung
                      </button>

                      <button
                        onClick={() => setDetailRowId(isExpanded ? null : row.id)}
                        disabled={!row.last_feedback}
                        title={row.last_feedback ? 'Details anzeigen' : 'Noch keine Rückmeldung vorhanden'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '5px 10px',
                          backgroundColor: isExpanded ? '#DBEAFE' : 'white',
                          border: '1px solid #D1D5DB', borderRadius: '4px',
                          cursor: row.last_feedback ? 'pointer' : 'not-allowed',
                          fontSize: '11px', color: '#374151',
                          opacity: row.last_feedback ? 1 : 0.5
                        }}
                      >
                        {isExpanded ? 'Zuklappen' : 'Details'}
                      </button>

                      {row.state !== 'cancelled' && (
                        <button
                          onClick={() => cancelEau(row)}
                          disabled={busy}
                          title="eAU-Anfrage stornieren"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            padding: '5px 10px',
                            backgroundColor: 'white', border: '1px solid #FECACA',
                            borderRadius: '4px',
                            cursor: busy ? 'not-allowed' : 'pointer',
                            fontSize: '11px', color: '#991B1B'
                          }}
                        >
                          {busy && actionType === 'cancel' ? (
                            <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <Trash2 size={12} />
                          )}
                        </button>
                      )}
                        </>
                      )}
                    </div>
                  </div>

                  {isExpanded && row.last_feedback && (
                    <div style={{ marginTop: '12px' }}>
                      {renderFeedbackDetail(row.last_feedback as DatevEauFeedback)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
